import React from 'react';
import clsx from 'clsx';

import BaseComponent from 'pa-react/dist/components/BaseComponent';
import Loadable from 'pa-react/dist/behaviors/loadable/Loadable';
import URLParametersService from 'pa-react/dist/util/URLParametersService';

import './SupplierRequests.css';
import Button from 'components/button/Button';
import SupplierRequestConfirmationDialog from 'components/dialog/SupplierRequestConfirmationDialog';
import Tree, { TreeDataProvider } from 'components/tree/Tree';
import Tooltip from 'components/tooltip/Tooltip';
import TooltipIcon from 'components/icon/TooltipIcon';
import SupplierRequestsService from './SupplierRequestsService';
import DecisionDialog from 'components/dialog/DecisionDialog';
import TextField from 'components/input/TextField';

//

/**
 * @augments React.Component<PropTypes, any>
 */
export default class SupplierRequests extends BaseComponent {
  constructor(props) {
    super(props);
    this.generateLabels = this.generateLabels.bind(this);
    this.treeDataProvider = new TreeDataProvider({ data: props.supplierRequestsTree });
    this.offeringKey = URLParametersService.getParentWindowParameter('okey');

    this.state = {
      selectedSupplier: undefined,
      approveSupplierRequest: false,
      openApproveOrRejectDialog: false,
      comments: '',
      leadCompanyKeyValue: -1,
      loading: false
    };

    this.registerService(SupplierRequestsService);
  }

  generateLabels(data) {
    if (data.personKey) {
      return (
        <React.Fragment>
          <Tooltip title={this._generateTooltip(data)}>
            <div key="label" className="supplier-request-label">
              {data.name}
            </div>
          </Tooltip>
          <div className="supplier-request-icons">
            <TooltipIcon
              iconTestId="approve-icon"
              iconClassName="approve-icon"
              iconName="thumb_up"
              onIconClick={() => this.onApprove(data)}
              title="Approve"
            />
            <TooltipIcon
              iconTestId="deny-icon"
              iconClassName="deny-icon"
              iconName="thumb_down"
              onIconClick={() => {
                this.setState({ approveSupplierRequest: false, selectedSupplier: data });
              }}
              title="Deny"
            />
          </div>
        </React.Fragment>
      );
    } else {
      return (
        <div key="label" className="supplier-request-label">
          {data.name}
        </div>
      );
    }
  }

  _generateTooltip(record) {
    const toolTipData = {
      Name: record.name,
      'Business Title': record.businessTitle,
      'Cell Phone': record.cellPhone,
      'Work Phone': record.workPhone,
      Email: record.email
    };

    if (record.personKey) {
      return (
        <span className="personToolTip">
          {Object.entries(toolTipData).map(([key, value]) => {
            return (
              <React.Fragment key={key}>
                <strong>{key}: </strong>
                {value}
                <br />
              </React.Fragment>
            );
          })}
        </span>
      );
    } else {
      return record.name;
    }
  }

  render() {
    const {
      openApproveOrRejectDialog,
      approveSupplierRequest,
      comments,
      loading,
      selectedSupplier,
      supplierBidTeam,
      approveButtonClicked
    } = this.state;
    const approveOrRejectDialogTitle = approveButtonClicked === true ? 'Approve' : 'Reject';

    return (
      <div
        className={clsx('supplier-requests', loading && 'supplier-requests-loading')}
        data-testid="supplier-requests"
      >
        <Loadable
          className="supplier-request-loading-mask"
          loading={loading}
          loadingMessage="Updating Supplier Requests..."
        >
          {this.props.supplierRequestsTree.length > 1 ? (
            <>
              <div className="supplier-request-all-button-container">
                <Button className="supplier-request-all-button" onClick={() => this.onApproveAll()}>
                  Approve All
                </Button>
                <Button className="supplier-request-all-button" onClick={() => this.onRejectAll()}>
                  Reject All
                </Button>
              </div>
              <DecisionDialog
                title={`${approveOrRejectDialogTitle} All Supplier Requests`}
                open={openApproveOrRejectDialog}
                actionButtons={[
                  <Button className="supplier-request-cancel-button" onClick={this.onCancel}>
                    Cancel
                  </Button>,
                  <Button
                    className="supplier-request-submit-button"
                    onClick={() => this.onApproveOrRejectAllSubmit(approveButtonClicked)}
                  >
                    Submit
                  </Button>
                ]}
                content={
                  <>
                    <ul className="supplier-request-confirmation-dialog-bulleted-points">
                      <li className="supplier-request-confirmation-dialog-item">
                        {`Clicking "Submit" will ${approveOrRejectDialogTitle} all requests for access to this bid event`}
                      </li>
                      <li className="supplier-request-confirmation-dialog-item">
                        Optional comments can be included below:
                      </li>
                    </ul>
                    <div>
                      <TextField
                        className="supplier-request-confirmation-dialog-content-textarea"
                        inputProps={{ maxLength: 1000 }}
                        label="Comments"
                        multiline
                        onChange={event => this.setState({ comments: event.target.value })}
                        rows={3}
                        value={comments}
                        variant="outlined"
                      />
                    </div>
                  </>
                }
                data-testid="supplier-request-confirmation-dialog"
                maxWidth="md"
              />
            </>
          ) : (
            <></>
          )}
          {this.props.supplierRequestsTree.length !== 0 ? (
            <Tree
              className="supplier-request-tree"
              treeDataProvider={this.treeDataProvider}
              getLabel={this.generateLabels}
            />
          ) : (
            <div className="supplier-request-no-request">
              <span className="supplier-request-no-request-message">No Supplier Requests Found</span>
            </div>
          )}
          {selectedSupplier && (
            <SupplierRequestConfirmationDialog
              actionButtons={[
                <Button className="supplier-request-cancel-button" onClick={this.onCancel}>
                  Cancel
                </Button>,
                <Button className="supplier-request-submit-button" onClick={this.onSubmit}>
                  Submit
                </Button>
              ]}
              approveSupplierRequest={approveSupplierRequest}
              bidTeams={this.props.bidTeams}
              comments={comments}
              onBidTeamSelectChange={leadCompanyKeyValue => this.setState({ leadCompanyKeyValue })}
              onTextFieldChange={event => this.setState({ comments: event.target.value })}
              open={!!selectedSupplier}
              supplier={selectedSupplier}
              supplierBidTeam={supplierBidTeam}
            />
          )}
        </Loadable>
      </div>
    );
  }
}
